import type { FC, ReactNode } from 'react';
import './styles.css';
import { safeAbsurd } from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import classNames from 'classnames';
import { ReferencedTestDetail } from 'components/SecuritySchema/ReferencedTestDetail';
import { RuleChainDetail } from 'components/SecuritySchema/RuleChainDetail';
import { SecurityEntityList } from 'components/SecuritySchema/SecurityEntityList';
import { SecuritySchemaBreadcrumbs } from 'components/SecuritySchema/SecuritySchemaBreadcrumbs';
import type { SecuritySchemaDataSource } from 'components/SecuritySchema/model/SecuritySchemaDataSource';
import type { SecuritySchemaEntryPoint } from 'components/SecuritySchema/model/SecuritySchemaEntryPoint';
import {
  SecuritySchemaContext,
  useSecuritySchemaContextSelectors,
} from 'components/SecuritySchema/model/SecuritySchemaModel';
import {
  isListingScope,
  isReferencedTestScope,
  isRuleChainScope,
} from 'components/SecuritySchema/model/SecuritySchemaScope';
import { useSecuritySchema } from 'components/SecuritySchema/model/useSecuritySchema';
import { useLocale } from 'containers/App/LocaleContext';
import { LoaderAndText } from 'containers/LoaderAndText/LoaderAndText';

type SecuritySchemaViewProps = {
  breadcrumbsAddon?: ReactNode;
  className?: string;
};

type SecuritySchemaProps = SecuritySchemaViewProps & {
  entryPoint: SecuritySchemaEntryPoint;
  highlightedRuleLabel?: string;
  dataSource: SecuritySchemaDataSource;
};

const SecuritySchemaView: FC<SecuritySchemaViewProps> = (props) => {
  const msg = useLocale();

  const ctxProps = useSecuritySchemaContextSelectors({
    loadingState: prop('loadingState'),
    deviceSecurity: prop('deviceSecurity'),
    scope: prop('scope'),
  });

  return (
    <div className={classNames('flex flex-col p-5', props.className)}>
      <div className='security-tree__header flex flex-row items-center pb-5'>
        <div className='flex flex-1 flex-row'>
          <SecuritySchemaBreadcrumbs />
        </div>
        {props.breadcrumbsAddon}
      </div>
      <div className='flex flex-1 flex-col'>
        {/* biome-ignore lint/complexity/noExcessiveCognitiveComplexity: TBD eventually simplify */}
        {(() => {
          switch (ctxProps.loadingState) {
            case 'LOADING':
              return <LoaderAndText />;
            case 'ERROR':
              return (
                <p className='text-danger mt-lgu-10 text-center'>
                  {msg.securitySchema.loadError}
                </p>
              );
            case 'LOADED':
              if (ctxProps.deviceSecurity !== null) {
                if (ctxProps.scope === null) {
                  return (
                    <p className='text-danger mt-lgu-10 text-center'>
                      {msg.securitySchema.noScope}
                    </p>
                  );
                }
                if (isRuleChainScope(ctxProps.scope)) {
                  return <RuleChainDetail />;
                }
                if (isListingScope(ctxProps.scope)) {
                  return <SecurityEntityList />;
                }
                if (isReferencedTestScope(ctxProps.scope)) {
                  return <ReferencedTestDetail />;
                }
                safeAbsurd(ctxProps.scope);
                return null;
              }
              return (
                <p className='text-danger mt-lgu-10 text-center'>
                  {msg.securitySchema.noSecurityForHost}
                </p>
              );
            default:
              safeAbsurd(ctxProps.loadingState, 'unknown loading state');
              return null;
          }
        })()}
      </div>
    </div>
  );
};

export const SecuritySchema: FC<SecuritySchemaProps> = (props) => {
  const model = useSecuritySchema({
    entryPoint: props.entryPoint,
    highlightedRuleLabel: props.highlightedRuleLabel,
    dataSource: props.dataSource,
  });

  return (
    <SecuritySchemaContext.Provider value={model}>
      <SecuritySchemaView
        breadcrumbsAddon={props.breadcrumbsAddon}
        className={props.className}
      />
    </SecuritySchemaContext.Provider>
  );
};
